import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../../components/Modal';
import PermissionLink from '../../components/PermissionLink';
import SearchSelect from '../../components/SearchSelect';
import { Device, createDevice, updateDevice } from '../../api/device';
import { listProcessArchsAdmin, ProcessArch } from '../../api/dict';
import {
    SchemaGroup,
    deviceSchemaClient,
    groupAppliesToDevice,
    listDeviceExtValues,
    parseGroupFields,
    upsertDeviceExtValue,
} from '../../api/schema';
import { alertApprovalIfPending, isApprovalPending, ApiError } from '../../api/request';
import {
    CAPABILITY_GROUP,
    SchemaFieldInput,
    buildExtPatch,
    extValueToInput,
    useDictOptions,
    withListTypes,
} from './deviceSchemaForm';
import { confirmDialog } from '../../utils/dialog';

// 新建 / 编辑 设备: 身份字段固定 (device_key/名称/机型/工艺架构/状态/备注),
// 能力字段 Schema 驱动 — 选定工艺架构后按其框架模板 (device_schema · capability 组) 动态渲染,
// 值统一落 device_ext_value (2026-08-14 store=column 机制移除, 设备主表只收身份字段).
// 复杂工况 (optics/timing/motion/modes/校准曲线) 仍走 YAML 导入.
// editing=null 新建模式; editing=Device 编辑模式 (device_key 锁定不可改).
// 2026-09-21: 机型 (family) 从表单撤掉 —— 新建设备即新建机型 (向导侧已不选机型), 机型字典
// 不再是录入前置; 编辑时 family 原样保留不动。这一格改放内部编号 (internalKey)。
interface Props {
    open: boolean;
    editing: Device | null;
    onClose: () => void;
    onSaved: () => void;
}

const STATUS_OPTS = [
    { value: 'enabled', label: '启用' },
    { value: 'disabled', label: '禁用' },
];

const DeviceCreateModal = ({ open, editing, onClose, onSaved }: Props) => {
    const isEdit = !!editing;

    const [deviceKey, setDeviceKey] = useState('');
    const [name, setName] = useState('');
    // family 不再在表单里露出 (2026-09-21 建设备即建机型): 编辑时原样回传, 不动它的值
    const [family, setFamily] = useState('');
    const [internalKey, setInternalKey] = useState('');
    const [archCode, setArchCode] = useState('');
    const [status, setStatus] = useState('enabled');
    const [remark, setRemark] = useState('');

    const [archs, setArchs] = useState<ProcessArch[]>([]);
    const [groups, setGroups] = useState<SchemaGroup[]>([]);
    // 能力字段当前 ext 存量值 (编辑模式回填) 与用户改动 (undefined=未动, 显示基线值)
    const [extVals, setExtVals] = useState<Record<string, unknown>>({});
    const [capEdits, setCapEdits] = useState<Record<string, string>>({});
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open) return;
        setError('');
        setCapEdits({});
        setExtVals({});
        listProcessArchsAdmin()
            .then(setArchs)
            .catch(() => setArchs([]));
        deviceSchemaClient
            .list()
            .then(setGroups)
            .catch(() => setGroups([]));
        if (editing) {
            setDeviceKey(editing.deviceKey);
            setName(editing.name || '');
            setFamily(editing.family);
            setInternalKey(editing.internalKey || '');
            setArchCode(editing.archCode);
            setStatus(editing.status || 'enabled');
            setRemark(editing.remark || '');
            listDeviceExtValues(editing.deviceKey)
                .then((vals) => {
                    const v = vals.find((x) => x.groupKey === CAPABILITY_GROUP);
                    try {
                        setExtVals(v ? (JSON.parse(v.params) as Record<string, unknown>) : {});
                    } catch {
                        setExtVals({});
                    }
                })
                .catch(() => setExtVals({}));
        } else {
            setDeviceKey('');
            setInternalKey('');
            setName('');
            setFamily('');
            setArchCode('');
            setStatus('enabled');
            setRemark('');
        }
    }, [open, editing]);

    // 当前选中框架的 capability 模板 (与工况页 DeviceCapabilityCard 同一查找规则);
    // 组限定了设备清单时只对清单内设备渲染 —— 新建的设备还不在任何清单里, 故这类组不出现
    const archOpts = useMemo(
        () => archs.map((a) => ({ value: a.code, label: `${a.icon} ${a.label}` })),
        [archs],
    );
    const capGroup = useMemo(
        () =>
            groups.find(
                (g) =>
                    g.isEnabled &&
                    g.groupKey === CAPABILITY_GROUP &&
                    g.archCode === archCode &&
                    groupAppliesToDevice(g, deviceKey),
            ) || null,
        [groups, archCode, deviceKey],
    );
    const capFields = useMemo(
        () => withListTypes(parseGroupFields(capGroup?.fields), extVals),
        [capGroup, extVals],
    );
    const dictOpts = useDictOptions(capFields.map((f) => f.optionsSource));

    // 改绑模板 (编辑时换了工艺架构): 后端会清空该设备原模板下的 device_ext_value,
    // 故表单也不拿旧模板的 ext 值当基线 — 同名字段在新模板下含义未必相同, 静默继承比留空更危险.
    const archChanged = isEdit && archCode !== '' && archCode !== editing!.archCode;

    // 字段显示值: 用户改过取改动; 否则编辑模式回填 ext 存量基线 (改绑模板时不回填, 见 archChanged)
    const capValueOf = (fieldKey: string): string => {
        const edited = capEdits[fieldKey];
        if (edited !== undefined) return edited;
        if (!editing || archChanged) return '';
        return extValueToInput(extVals[fieldKey]);
    };

    const onSubmit = async () => {
        if (!deviceKey.trim() || !archCode) {
            setError('device_key / 工艺架构 都必填');
            return;
        }
        if (
            archChanged &&
            !await confirmDialog(
                `确认把 ${editing!.deviceKey} 的工艺架构从 ${editing!.archCode} 改为 ${archCode}?\n` +
                    '该设备在原模板下已填的动态字段值 (各字段组) 会被清空且无法恢复, 需按新模板重填.',
            )
        ) {
            return;
        }
        const raw = Object.fromEntries(capFields.map((f) => [f.key, capValueOf(f.key)]));
        const { extPatch, invalid } = buildExtPatch(capFields, raw);
        if (invalid) {
            setError(`「${invalid.field.label}」不是有效数字: ${invalid.raw}`);
            return;
        }
        setBusy(true);
        setError('');
        try {
            const payload: Partial<Device> & { deviceKey: string; family: string; archCode: string } = {
                deviceKey: deviceKey.trim(),
                name: name.trim(),
                // 机型不在表单里: 编辑保持原值; 新建 (老路径, 入口已下线) 以 deviceKey 兜底,
                // 与套件"建设备即建机型"同口径
                family: family.trim() || deviceKey.trim(),
                internalKey: internalKey.trim(),
                archCode,
                status,
                remark: remark.trim() || undefined,
            };
            const res = isEdit
                ? await updateDevice(editing!.deviceKey, payload)
                : await createDevice(payload);
            let message = res.message || '';
            // ext 字段: 编辑=整组覆盖 (清空即去掉); 新建=有值才写.
            // 新建走审批时设备尚未落库, ext 无处可写 — 审批通过后到工况页补录.
            const skipExt = !isEdit && (isApprovalPending(message) || Object.keys(extPatch).length === 0);
            if (capGroup && !skipExt) {
                const res2 = await upsertDeviceExtValue(deviceKey.trim(), CAPABILITY_GROUP, JSON.stringify(extPatch));
                message = res2.message || message;
            }
            // 走审批流时数据未变, onSaved 的刷新只是回读原值
            onSaved();
            onClose();
            alertApprovalIfPending(message);
        } catch (e) {
            setError(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={isEdit ? `编辑设备 · ${editing?.deviceKey}` : '新增设备'}
            width={640}
        >
            <div className="space-y-3">
                <div className="text-xs text-slate-500">
                    身份字段 + 能力字段 (按工艺架构模板动态渲染); 复杂工况 (光斑 / 时序 / 速度模式 / 校准曲线) 用 <b>导入 YAML</b> 更高效.
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="field-label">
                            <span className="text-rose-500">* </span>device_key (业务主键)
                        </label>
                        <input
                            className={`inp font-mono ${isEdit ? 'bg-slate-50' : ''}`}
                            value={deviceKey}
                            onChange={(e) => setDeviceKey(e.target.value)}
                            placeholder="如 S1-10W / DQ003-细切刀"
                            disabled={isEdit}
                        />
                    </div>
                    <div>
                        <label className="field-label">设备名称 (与源数据一致)</label>
                        <input
                            className="inp"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="如 F1 Ultra / M2 / DT002"
                        />
                    </div>
                    <div>
                        <label className="field-label">
                            <span className="text-rose-500">* </span>工艺架构
                        </label>
                        <SearchSelect
                            className="w-full"
                            value={archCode}
                            onChange={(v) => {
                                setArchCode(v);
                                // 机型候选按架构分域, 换架构后旧值不再适用
                                setFamily('');
                            }}
                            placeholder="请选择"
                            options={archOpts}
                        />
                        {archChanged && (
                            <div className="mt-1 text-[11px] text-amber-600">
                                改绑模板: 保存后 {editing!.archCode} 模板下已填的动态字段值会被清空, 需按新模板重填
                            </div>
                        )}
                    </div>
                    <div>
                        <label className="field-label">内部编号 (internalKey)</label>
                        <input
                            className="inp font-mono w-full"
                            value={internalKey}
                            onChange={(e) => setInternalKey(e.target.value)}
                            placeholder="可选"
                        />
                    </div>
                    <div>
                        <label className="field-label">状态</label>
                        <SearchSelect
                            className="w-full"
                            value={status}
                            onChange={(v) => setStatus(v)}
                            options={STATUS_OPTS}
                        />
                    </div>
                    <div>
                        <label className="field-label">备注</label>
                        <input
                            className="inp"
                            value={remark}
                            onChange={(e) => setRemark(e.target.value)}
                            placeholder="可选"
                        />
                    </div>
                </div>

                {/* 能力字段: 定义来自所选框架的 capability 模板, 与工况页「设备能力」卡片同源同规则 */}
                {archCode && (
                    <div className="pt-2 border-t border-slate-100">
                        <div className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-2">
                            能力字段
                            <span className="tag bg-brand-50 text-brand-700 font-normal text-[10px]">Schema 驱动</span>
                            <PermissionLink resource="device_schema" verb="update" hide
                                to={`/devices/templates?focus=${encodeURIComponent(archCode)}`}
                                className="tag bg-slate-100 text-slate-500 font-normal text-[10px] hover:bg-brand-50 hover:text-brand-600"
                                title="表单按该框架模板渲染 · 点击查看/管理模板"
                            >
                                引用模板: {archCode} ↗
                            </PermissionLink>
                        </div>
                        {capFields.length === 0 ? (
                            <div className="text-xs text-slate-400">
                                该框架暂无能力字段模板, 可在 Schema管理 → 设备模板 为「{archCode}」新建字段组
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 gap-3">
                                {capFields.map((f) => (
                                    <div key={f.key}>
                                        <label className="field-label">
                                            {f.label}
                                            {f.unit ? ` (${f.unit})` : ''}
                                        </label>
                                        <SchemaFieldInput
                                            field={f}
                                            value={capValueOf(f.key)}
                                            onChange={(v) => setCapEdits((prev) => ({ ...prev, [f.key]: v }))}
                                            dictOpts={dictOpts}
                                            selectClassName="w-full"
                                            inputClassName="inp w-full"
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {error && (
                    <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                        {error}
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                    <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>
                        取消
                    </button>
                    <button
                        type="button"
                        className="btn-primary"
                        onClick={onSubmit}
                        disabled={busy}
                    >
                        {busy ? '保存中…' : isEdit ? '保存' : '创建'}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default DeviceCreateModal;
